// src/components/layout/RightPanel/UniversalEditorExtensions.jsx
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { tags as t } from '@codemirror/highlight';
import { lineNumbers } from '@codemirror/view';
import { history, historyKeymap } from '@codemirror/commands';
import { keymap } from '@codemirror/view';
import { defaultKeymap } from '@codemirror/commands';
import { search, searchKeymap } from '@codemirror/search'; // NEW: Import search functionality
import { CONTENT_TYPES } from '@/managers/PanelManager';
import { loadLanguageSupport } from './LanguageLoader';
import { createMermaidExtension } from './MermaidExtension';

/* ========================
 * createUniversalTheme
 * Uses your global --background-dark for consistency
 * ======================== */
const createUniversalTheme = (fontSize = 14) => {
  const gutterBaseWidth = Math.max(2, Math.round(fontSize * 2.5));

  // All pulled from your CSS theme
  const bgColor           = 'var(--background-dark)';
  const textColor         = 'var(--on-surface)';
  const gutterBgColor     = 'var(--background-dark)';
  const gutterTextColor   = 'var(--on-surface-dim)';
  const borderColor       = 'var(--border-color)';
  const selectionBgColor  = 'rgba(var(--on-surface-rgb), 0.15)';
  const caretColor        = 'var(--on-surface)';

  return EditorView.theme({
    '&': {
      height: '100%',
      width: '100%',
      fontSize: `${fontSize}px`,
      fontFamily: 'var(--code-font-family)',
      backgroundColor: bgColor,
      color: textColor,
    },
    '.cm-content': {
      padding: '0.75rem 0.5rem',
      minHeight: '100%',
      caretColor: caretColor,
      lineHeight: '1.5',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    '.cm-line': {
      padding: '0 0.5rem',
      lineHeight: '1.5',
    },
    '.cm-focused': {
      outline: 'none',
    },
    '.cm-gutters': {
      backgroundColor: gutterBgColor,
      borderRight: `1px solid ${borderColor}`,
      color: gutterTextColor,
      paddingLeft: '0.25rem',
      paddingRight: '0.25rem',
      minWidth: `${gutterBaseWidth}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '.cm-lineNumbers': {
      minWidth: `${gutterBaseWidth - 4}px`,
    },
    '.cm-lineNumbers .cm-gutterElement': {
      padding: '0 0.25rem',
      minWidth: '1em',
      textAlign: 'center',
      boxSizing: 'content-box',
      lineHeight: '1.5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '.cm-scroller': {
      fontFamily: 'inherit',
      lineHeight: '1.5',
      overflow: 'auto',
    },
    '.cm-cursor, .cm-dropCursor': {
      borderLeftColor: caretColor,
      borderLeftWidth: '2px',
    },
    '.cm-selectionBackground, .cm-content ::selection': {
      backgroundColor: selectionBgColor,
    },
    '&.cm-focused .cm-selectionBackground': {
      backgroundColor: selectionBgColor,
    },
    // NEW: Theme-aware styling for the search panel
    '.cm-search': {
        backgroundColor: 'var(--surface)',
        borderColor: 'var(--border-color)',
    },
    '.cm-search input': {
        backgroundColor: 'var(--background-dark)',
        borderColor: 'var(--border-color)',
        color: 'var(--on-surface)',
    },
     '.cm-search button': {
        color: 'var(--on-surface-dim)',
        border: '1px solid var(--border-color)',
        background: 'var(--surface-dark)'
    },
    '.cm-search button:hover': {
        backgroundColor: 'var(--surface)',
        color: 'var(--on-surface)',
    }
  });
};

const codeTheme = EditorView.theme({
  '.cm-editor[data-content-type="code"] .cm-content': {
    fontFamily: 'var(--code-font-family, monospace)',
    // Background color for code content area will be inherited from createUniversalTheme's bgColor
  },
  '&.cm-editor[data-content-type="code"]': {}
});

const mermaidTheme = EditorView.theme({
  '.cm-editor[data-content-type="mermaid"] .cm-content': {
    fontFamily: 'var(--code-font-family, monospace)',
    // Background color for mermaid content area will be inherited from createUniversalTheme's bgColor
  },
  '&.cm-editor[data-content-type="mermaid"]': {}
});

const universalHighlighting = HighlightStyle.define([
  { tag: t.comment,       color: '#6A9955' },
  { tag: t.keyword,       color: '#569CD6' },
  { tag: t.string,        color: '#CE9178' },
  { tag: t.number,        color: '#B5CEA8' },
  { tag: t.operator,      color: '#D4D4D4' },
  { tag: t.punctuation,   color: '#D4D4D4' },
  { tag: t.function(t.variableName), color: '#DCDCAA' },
  { tag: t.variableName,  color: '#9CDCFE' },
  { tag: t.typeName,      color: '#4EC9B0' },
  { tag: t.className,     color: '#4EC9B0' },
  { tag: t.propertyName,  color: '#9CDCFE' },
  { tag: t.meta,          color: '#569CD6' },
  { tag: t.heading,       fontWeight: 'bold', color: '#569CD6' },
  { tag: t.strong,        fontWeight: 'bold' },
  { tag: t.emphasis,      fontStyle: 'italic' },
  { tag: t.link,          color: '#569CD6', textDecoration: 'underline' },
  { tag: t.quote,         color: '#6A9955', fontStyle: 'italic' },
  { tag: t.monospace,     fontFamily: 'var(--code-font-family)', backgroundColor: 'rgba(128,128,128,0.1)', padding: '0.1em 0.3em', borderRadius: '3px' },
]);

const lineWrapping = EditorView.lineWrapping;

export const createUniversalExtensions = async (contentType, metadata = {}, options = {}) => {
  const { onChange, readOnly = false, fontSize = 14 } = options;

  const baseExtensions = [
    createUniversalTheme(fontSize), // This sets the main editor BG and gutter to background-dark
    syntaxHighlighting(universalHighlighting),
    lineWrapping,
    history(),
    keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]), // UPDATED: Added search keymap
    EditorState.readOnly.of(readOnly),
    EditorView.editable.of(!readOnly),
    EditorView.contentAttributes.of({ 'data-content-type': contentType }),
    search({ top: true }), // NEW: Added search panel functionality
  ];

  if (onChange) {
    baseExtensions.push(
      EditorView.updateListener.of(update => {
        if (update.docChanged) onChange(update.state.doc.toString());
      })
    );
  }

  switch (contentType) {
    case CONTENT_TYPES.CODE: {
      const language = metadata.language || 'javascript';
      const languageSupport = await loadLanguageSupport(language);
      return [
        ...baseExtensions,
        codeTheme, // Applies code-specific font
        languageSupport,
        lineNumbers(), // Gutter BG is now background-dark from createUniversalTheme
      ];
    }
    case CONTENT_TYPES.MERMAID: {
      const mermaidExt = await createMermaidExtension();
      return [
        ...baseExtensions,
        mermaidTheme, // Applies mermaid-specific font
        mermaidExt,
        lineNumbers(), // Gutter BG is now background-dark from createUniversalTheme
      ];
    }
    default: {
      const support = await loadLanguageSupport('plaintext');
      return [
        ...baseExtensions,
        support,
      ];
    }
  }
};